import { useState } from 'react';

/**
 * Custom hook for managing Decision Support state
 */
const useDecisionSupport = () => {
  const getDefaultDecisionSupport = () => ({
    civilians: {
      fatalities: { reportPeriod: '', totalToDate: '' },
      injuriesSick: { reportPeriod: '', totalToDate: '' },
      trappedNeedRescue: { reportPeriod: '', totalToDate: '' },
      missing: { reportPeriod: '', totalToDate: '' },
      evacuated: { reportPeriod: '', totalToDate: '' },
      shelteringInPlace: { reportPeriod: '', totalToDate: '' },
      inTempShelters: { reportPeriod: '', totalToDate: '' },
      receivedMassImmunizations: { reportPeriod: '', totalToDate: '' },
      requireImmunizations: { reportPeriod: '', totalToDate: '' },
      inQuarantine: { reportPeriod: '', totalToDate: '' }
    },
    responders: {
      fatalities: { reportPeriod: '', totalToDate: '' },
      injuriesSick: { reportPeriod: '', totalToDate: '' },
      trappedNeedRescue: { reportPeriod: '', totalToDate: '' },
      missing: { reportPeriod: '', totalToDate: '' },
      evacuated: { reportPeriod: '', totalToDate: '' },
      shelteringInPlace: { reportPeriod: '', totalToDate: '' },
      inTempShelters: { reportPeriod: '', totalToDate: '' },
      receivedMassImmunizations: { reportPeriod: '', totalToDate: '' },
      requireImmunizations: { reportPeriod: '', totalToDate: '' },
      inQuarantine: { reportPeriod: '', totalToDate: '' }
    },
    lifeSafetyThreatMgmt: {
      noLifeSafetyIssues: false,
      massNotifications: false,
      evacuationsInProgress: false,
      evacuationsComplete: false,
      noEvacuations: false,
      shelterInPlace: false,
      rescueInProgress: false,
      repopulationInProgress: false,
      massImmunizationInProgress: false,
      massImmunizationComplete: false,
      quarantineInProgress: false,
      areaRestrictionInEffect: false
    },
    lifeSafetyHealthRemarks: '',
    weatherConcerns: '',
    projectedActivity: {
      twelveHours: '',
      twentyFourHours: '',
      fortyEightHours: '',
      seventyTwoHours: '',
      afterSeventyTwoHours: ''
    }
  });

  const [decisionSupportEntries, setDecisionSupportEntries] = useState([]);
  const [currentDecisionSupport, setCurrentDecisionSupport] = useState(getDefaultDecisionSupport());
  const [editingDecisionSupport, setEditingDecisionSupport] = useState(null);

  // Helper function to calculate totals
  const calculateTotals = (category) => {
    let reportPeriodTotal = 0;
    let totalToDateSum = 0;
    Object.values(category).forEach(item => {
      if (item && typeof item === 'object' && 'reportPeriod' in item) {
        reportPeriodTotal += parseInt(item.reportPeriod) || 0;
        totalToDateSum += parseInt(item.totalToDate) || 0;
      }
    });
    return { reportPeriodTotal, totalToDateSum };
  };

  // Update functions for nested state
  const updateCiviliansData = (key, field, value) => {
    setCurrentDecisionSupport(prev => ({
      ...prev,
      civilians: {
        ...prev.civilians,
        [key]: {
          ...prev.civilians[key],
          [field]: value
        }
      }
    }));
  };

  const updateRespondersData = (key, field, value) => {
    setCurrentDecisionSupport(prev => ({
      ...prev,
      responders: {
        ...prev.responders,
        [key]: {
          ...prev.responders[key],
          [field]: value
        }
      }
    }));
  };

  const updateLifeSafetyThreat = (key, value) => {
    setCurrentDecisionSupport(prev => ({
      ...prev,
      lifeSafetyThreatMgmt: {
        ...prev.lifeSafetyThreatMgmt,
        [key]: value
      }
    }));
  };

  const updateProjectedActivity = (key, value) => {
    setCurrentDecisionSupport(prev => ({
      ...prev,
      projectedActivity: {
        ...prev.projectedActivity,
        [key]: value
      }
    }));
  };

  const addDecisionSupport = () => {
    if (editingDecisionSupport) {
      setDecisionSupportEntries(prev => prev.map(d => 
        d.id === editingDecisionSupport.id 
          ? { ...currentDecisionSupport, id: d.id, timestamp: d.timestamp, updatedAt: new Date().toISOString() }
          : d
      ));
      setEditingDecisionSupport(null);
    } else {
      const newEntry = {
        ...currentDecisionSupport,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setDecisionSupportEntries(prev => [...prev, newEntry]);
    }
    
    setCurrentDecisionSupport(getDefaultDecisionSupport());
  };

  const editDecisionSupport = (entry) => {
    setCurrentDecisionSupport(entry);
    setEditingDecisionSupport(entry);
  };

  const cancelEditDecisionSupport = () => {
    setCurrentDecisionSupport(getDefaultDecisionSupport());
    setEditingDecisionSupport(null);
  };

  const deleteDecisionSupport = (entryId) => {
    if (window.confirm('Are you sure you want to delete this decision support entry?')) {
      setDecisionSupportEntries(prev => prev.filter(d => d.id !== entryId));
    }
  };

  return {
    // State
    decisionSupportEntries,
    setDecisionSupportEntries,
    currentDecisionSupport,
    setCurrentDecisionSupport,
    editingDecisionSupport,
    setEditingDecisionSupport,
    // Functions
    addDecisionSupport,
    editDecisionSupport,
    cancelEditDecisionSupport,
    deleteDecisionSupport,
    getDefaultDecisionSupport,
    // Helper functions
    calculateTotals,
    updateCiviliansData,
    updateRespondersData,
    updateLifeSafetyThreat,
    updateProjectedActivity
  };
};

export default useDecisionSupport;
